import React, { useState, useEffect } from 'react';
import {
  Table,
  Button,
  Modal,
  message,
  Space,
  Tag,
  Card,
  Descriptions,
  Image,
  Empty,
  Form,
  Select,
  Upload,
  Input,
  Pagination,
  Popconfirm,
} from 'antd';
import {
  ReloadOutlined,
  EyeOutlined,
  DollarOutlined,
  UploadOutlined,
  MessageOutlined,
  CheckCircleOutlined,
  PlusOutlined,
} from '@ant-design/icons';
import { apiClient } from '../../config/apiClient';
import MainLayout from '../layout/layout';
import dayjs from 'dayjs';
import { compressFile } from '../../utils/fileCompression';

const MESES = [
  { value: 1, label: 'Enero' }, { value: 2, label: 'Febrero' }, { value: 3, label: 'Marzo' },
  { value: 4, label: 'Abril' }, { value: 5, label: 'Mayo' }, { value: 6, label: 'Junio' },
  { value: 7, label: 'Julio' }, { value: 8, label: 'Agosto' }, { value: 9, label: 'Septiembre' },
  { value: 10, label: 'Octubre' }, { value: 11, label: 'Noviembre' }, { value: 12, label: 'Diciembre' },
];

const RegistroPagos = () => {
  const [registros, setRegistros] = useState([]);
  const [alumnos, setAlumnos] = useState([]);
  const [planes, setPlanes] = useState([]);
  const [tiposPago, setTiposPago] = useState([]);
  const [loading, setLoading] = useState(false);
  const [modalDetalleVisible, setModalDetalleVisible] = useState(false);
  const [modalRegistrarPagoVisible, setModalRegistrarPagoVisible] = useState(false);
  const [modalNuevoPagoVisible, setModalNuevoPagoVisible] = useState(false);
  const [registroSeleccionado, setRegistroSeleccionado] = useState(null);
  const [registroParaPago, setRegistroParaPago] = useState(null);
  const [comprobanteFile, setComprobanteFile] = useState(null);
  const [comprobanteFileNuevo, setComprobanteFileNuevo] = useState(null);
  const [formRegistrarPago] = Form.useForm();
  const [formNuevoPago] = Form.useForm();
  const planSeleccionadoNuevoPago = Form.useWatch('planId', formNuevoPago);
  const planNuevoPago = planes.find(p => p.pla_id === planSeleccionadoNuevoPago);
  const [isMobile, setIsMobile] = useState(() => window.innerWidth <= 768);
  const [filtroNombreAlumno, setFiltroNombreAlumno] = useState('');
  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: 10,
    total: 0,
  });

  // Cargar alumnos (para select en nuevo pago)
  const loadAlumnos = async () => {
    try {
      const response = await apiClient.get('/alumnos?pageSize=500');
      if (response.ok) {
        const result = await response.json();
        setAlumnos(result.data || []);
      }
    } catch (error) {
      console.error('Error al cargar alumnos:', error);
    }
  };

  // Cargar tipos de pago
  const loadTiposPago = async () => {
    try {
      const response = await apiClient.get('/tipo-pagos');
      if (response.ok) {
        const data = await response.json();
        setTiposPago(data);
      }
    } catch (error) {
      console.error('Error al cargar tipos de pago:', error);
    }
  };

  // Cargar planes de mensualidad
  const loadPlanes = async () => {
    try {
      const response = await apiClient.get('/planes');
      if (response.ok) {
        const data = await response.json();
        setPlanes((data || []).filter(plan => plan.pla_activo));
      }
    } catch (error) {
      console.error('Error al cargar planes:', error);
    }
  };

  // Cargar registros de pago con filtros y paginación
  const loadRegistros = async (nombreAlumno = null, page = 1, pageSize = 10) => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (nombreAlumno) params.append('nombreAlumno', nombreAlumno);
      params.append('page', page.toString());
      params.append('pageSize', pageSize.toString());
      
      const queryString = params.toString();
      const url = `/registro-pagos?${queryString}`;
      
      const response = await apiClient.get(url);
      if (response.ok) {
        const result = await response.json();
        setRegistros(result.data || []);
        setPagination({
          current: result.page || 1,
          pageSize: result.pageSize || 10,
          total: result.total || 0,
        });
      } else {
        message.error('Error al cargar los registros de pago');
      }
    } catch (error) {
      console.error('Error:', error);
      message.error('Error al cargar los registros de pago');
    } finally {
      setLoading(false);
    }
  };

  // Manejar cambio en filtro de nombre de alumno
  const handleFiltroNombreAlumnoChange = (e) => {
    const valor = e.target.value;
    setFiltroNombreAlumno(valor);
    // Debounce: esperar 500ms después de que el usuario deje de escribir
    clearTimeout(window.filtroNombreAlumnoTimeout);
    window.filtroNombreAlumnoTimeout = setTimeout(() => {
      setPagination(prev => ({ ...prev, current: 1 }));
      loadRegistros(valor || null, 1, pagination.pageSize);
    }, 500);
  };

  // Limpiar filtro
  const handleLimpiarFiltro = () => {
    setFiltroNombreAlumno('');
    setPagination(prev => ({ ...prev, current: 1 }));
    loadRegistros(null, 1, pagination.pageSize);
  };

  // Manejar cambio de página
  const handleTableChange = (newPagination) => {
    setPagination({
      current: newPagination.current,
      pageSize: newPagination.pageSize,
      total: pagination.total,
    });
    loadRegistros(
      filtroNombreAlumno || null,
      newPagination.current,
      newPagination.pageSize
    );
  };

  useEffect(() => {
    loadTiposPago();
    loadPlanes();
    loadAlumnos();
    loadRegistros(null, 1, 10);
  }, []);

  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth <= 768);
    };
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Convertir archivo a base64 (después de comprimir)
  const getBase64 = async (file) => {
    try {
      // Comprimir el archivo primero
      const compressedFile = await compressFile(file);
      return new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.readAsDataURL(compressedFile);
        reader.onload = () => resolve(reader.result);
        reader.onerror = error => reject(error);
      });
    } catch (error) {
      message.error(error.message || 'Error al procesar el archivo');
      throw error;
    }
  };

  // Descargar comprobante
  const handleDownloadComprobante = (comprobanteBase64, tipoPago) => {
    try {
      if (!comprobanteBase64) {
        message.error('No hay comprobante disponible');
        return;
      }

      // Determinar tipo MIME y extensión
      let mimeType = 'application/octet-stream';
      let extension = 'bin';
      
      // Verificar si tiene prefijo data: o es base64 puro
      if (comprobanteBase64.startsWith('data:image/')) {
        if (comprobanteBase64.includes('data:image/jpeg') || comprobanteBase64.includes('data:image/jpg')) {
          mimeType = 'image/jpeg';
          extension = 'jpg';
        } else if (comprobanteBase64.includes('data:image/png')) {
          mimeType = 'image/png';
          extension = 'png';
        } else {
          mimeType = 'image/png';
          extension = 'png';
        }
      } else if (comprobanteBase64.startsWith('data:application/pdf')) {
        mimeType = 'application/pdf';
        extension = 'pdf';
      } else {
        // Si no tiene prefijo data:, intentar detectar por el contenido base64
        // Los PDFs suelen empezar con JVBERi0 o %PDF
        // Las imágenes JPEG suelen empezar con /9j/
        // Las imágenes PNG suelen empezar con iVBORw0KG
        const base64Data = comprobanteBase64.split(',')[1] || comprobanteBase64;
        try {
          const decoded = atob(base64Data.substring(0, 20));
          if (decoded.startsWith('%PDF') || decoded.startsWith('JVBERi0')) {
            mimeType = 'application/pdf';
            extension = 'pdf';
          } else if (base64Data.startsWith('/9j/') || base64Data.startsWith('iVBORw0KG')) {
            // Intentar detectar PNG vs JPEG
            if (base64Data.startsWith('iVBORw0KG')) {
              mimeType = 'image/png';
              extension = 'png';
            } else {
              mimeType = 'image/jpeg';
              extension = 'jpg';
            }
          }
        } catch (e) {
          // Si no se puede decodificar, usar valores por defecto
          console.warn('No se pudo detectar el tipo de archivo, usando valores por defecto');
        }
      }

      // Extraer el base64 puro (sin prefijo data: si existe)
      const base64Data = comprobanteBase64.split(',')[1] || comprobanteBase64;
      
      // Convertir base64 a blob
      const byteCharacters = atob(base64Data);
      const byteNumbers = new Array(byteCharacters.length);
      for (let i = 0; i < byteCharacters.length; i++) {
        byteNumbers[i] = byteCharacters.charCodeAt(i);
      }
      const byteArray = new Uint8Array(byteNumbers);
      const blob = new Blob([byteArray], { type: mimeType });

      // Crear URL y descargar
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `comprobante_${tipoPago || 'pago'}_${Date.now()}.${extension}`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Error al descargar comprobante:', error);
      message.error('Error al descargar el comprobante');
    }
  };

  // Abrir modal de detalle
  const handleVerDetalle = (record) => {
    setRegistroSeleccionado(record);
    setModalDetalleVisible(true);
  };

  // Abrir modal para nuevo pago mensual
  const handleAbrirNuevoPago = () => {
    formNuevoPago.resetFields();
    const planesActivos = planes.filter(plan => plan.pla_activo);
    if (planesActivos.length === 1) {
      formNuevoPago.setFieldsValue({ planId: planesActivos[0].pla_id });
    }
    setComprobanteFileNuevo(null);
    setModalNuevoPagoVisible(true);
  };

  // Guardar nuevo pago mensual
  const handleSaveNuevoPago = async () => {
    try {
      const values = await formNuevoPago.validateFields();
      let comprobanteBase64 = null;
      let tipoPagoId = null;
      const metodoPago = values.metodoPago;

      if (metodoPago === 'TRANSFERENCIA') {
        if (!comprobanteFileNuevo) {
          message.error('El comprobante es obligatorio para pagos por transferencia');
          return;
        }
        comprobanteBase64 = await getBase64(comprobanteFileNuevo);
      }

      if (metodoPago !== 'PENDIENTE') {
        const tipoPago = tiposPago.find(tp => tp.tip_nombre === metodoPago);
        if (tipoPago) tipoPagoId = tipoPago.tip_id;
      }

      const dataToSend = {
        alumnoId: values.alumnoId,
        planId: values.planId,
        mes: values.mes,
        anio: values.anio,
        tipoPagoId,
        comprobante: comprobanteBase64,
        metodoPago,
      };

      const response = await apiClient.post('/registro-pagos-academia', dataToSend);
      if (response.ok) {
        const result = await response.json();
        message.success(result.message || 'Pago mensual registrado correctamente');
        setModalNuevoPagoVisible(false);
        formNuevoPago.resetFields();
        setComprobanteFileNuevo(null);
        loadRegistros(filtroNombreAlumno || null, pagination.current, pagination.pageSize);
      } else {
        const error = await response.json();
        message.error(error.message || 'Error al registrar el pago');
      }
    } catch (error) {
      if (error.errorFields) return;
      message.error('Error al registrar el pago');
    }
  };

  // Abrir modal para registrar pago (actualizar comprobante en pendiente)
  const handleRegistrarPago = (record) => {
    setRegistroParaPago(record);
    formRegistrarPago.resetFields();
    setComprobanteFile(null);
    setModalRegistrarPagoVisible(true);
  };

  // Aprobar pago pendiente (para pagos en efectivo)
  const handleAprobarPago = async (record) => {
    try {
      const response = await apiClient.put(`/registro-pagos-academia/${record.reg_id}/aprobar`);
      if (response.ok) {
        message.success('Pago aprobado correctamente');
        loadRegistros(filtroNombreAlumno || null, pagination.current, pagination.pageSize);
      } else {
        const error = await response.json();
        message.error(error.message || 'Error al aprobar el pago');
      }
    } catch (error) {
      console.error('Error:', error);
      message.error('Error al aprobar el pago');
    }
  };

  // Guardar registro de pago
  const handleSaveRegistrarPago = async () => {
    try {
      const values = await formRegistrarPago.validateFields();
      
      let comprobanteBase64 = null;
      let tipoPagoId = null;
      const metodoPago = values.metodoPago;

      // Si es TRANSFERENCIA, validar que haya comprobante
      if (metodoPago === 'TRANSFERENCIA') {
        if (!comprobanteFile) {
          message.error('El comprobante es obligatorio para pagos por transferencia');
          return;
        }
        comprobanteBase64 = await getBase64(comprobanteFile);
      }

      // Obtener el tipoPagoId según el método seleccionado
      if (metodoPago !== 'PENDIENTE') {
        const tipoPago = tiposPago.find(tp => tp.tip_nombre === metodoPago);
        if (tipoPago) {
          tipoPagoId = tipoPago.tip_id;
        }
      }

      const dataToSend = {
        tipoPagoId: tipoPagoId,
        comprobante: comprobanteBase64,
        metodoPago: metodoPago,
      };

      const response = await apiClient.put(`/registro-pagos-academia/${registroParaPago.reg_id}/registrar`, dataToSend);
      if (response.ok) {
        message.success('Pago registrado correctamente');
        setModalRegistrarPagoVisible(false);
        formRegistrarPago.resetFields();
        setComprobanteFile(null);
        setRegistroParaPago(null);
        loadRegistros(filtroNombreAlumno || null, pagination.current, pagination.pageSize);
      } else {
        const error = await response.json();
        message.error(error.message || 'Error al registrar el pago');
      }
    } catch (error) {
      console.error('Error:', error);
      if (error.errorFields) {
        return;
      }
      message.error('Error al registrar el pago');
    }
  };

  // Abrir WhatsApp con el número de teléfono
  const handleContactarWhatsApp = (telefono) => {
    if (!telefono) {
      message.warning('No hay número de teléfono disponible');
      return;
    }

    // Limpiar el número: quitar espacios, guiones, paréntesis y el signo +
    const numeroLimpio = telefono.replace(/[\s()+-]/g, '');
    
    // Si el número no empieza con código de país (Argentina es 54), asumimos que es local
    // Para Argentina, si empieza con 0, lo quitamos y agregamos 54
    let numeroFormateado = numeroLimpio;
    if (numeroLimpio.startsWith('0')) {
      numeroFormateado = '54' + numeroLimpio.substring(1);
    } else if (!numeroLimpio.startsWith('54') && numeroLimpio.length <= 10) {
      // Si no tiene código de país y parece ser un número local argentino, agregar 54
      numeroFormateado = '54' + numeroLimpio;
    }

    // Abrir WhatsApp
    const url = `https://wa.me/${numeroFormateado}`;
    window.open(url, '_blank');
  };

  // Formatear fecha
  const formatFecha = (fecha) => {
    if (!fecha) return '-';
    return dayjs(fecha).format('DD/MM/YYYY HH:mm');
  };

  // Obtener color del estado
  const getEstadoColor = (estado) => {
    switch (estado) {
      case 'PAGADO':
        return 'green';
      case 'PENDIENTE':
        return 'orange';
      case 'CANCELADO':
        return 'red';
      default:
        return 'default';
    }
  };

  // Formatear mes/año para mostrar
  const formatMesAnio = (reg_mes, reg_anio) => {
    if (reg_mes != null && reg_anio != null) {
      const m = MESES.find(x => x.value === reg_mes);
      return m ? `${m.label} ${reg_anio}` : `${reg_mes}/${reg_anio}`;
    }
    return '-';
  };

  // Columnas de la tabla
  const columns = [
    {
      title: 'Fecha',
      dataIndex: 'reg_fecha_creacion',
      key: 'reg_fecha_creacion',
      render: (fecha) => formatFecha(fecha),
      sorter: (a, b) => dayjs(a.reg_fecha_creacion).unix() - dayjs(b.reg_fecha_creacion).unix(),
    },
    {
      title: 'Mes/Año',
      key: 'reg_mes_anio',
      render: (_, record) => formatMesAnio(record.reg_mes, record.reg_anio),
    },
    {
      title: 'Alumno',
      dataIndex: 'alumno_nombre',
      key: 'alumno_nombre',
    },
    {
      title: 'Plan',
      dataIndex: 'plan_nombre',
      key: 'plan_nombre',
      render: (plan) => plan || '-',
    },
    {
      title: 'Teléfono',
      dataIndex: 'alumno_telefono',
      key: 'alumno_telefono',
      render: (telefono, record) => (
        <Space>
          <span>{telefono || '-'}</span>
          {telefono && (
            <Button
              type="link"
              icon={<MessageOutlined />}
              onClick={() => handleContactarWhatsApp(telefono)}
              style={{ 
                padding: 0, 
                height: 'auto',
                color: '#25D366'
              }}
            >
              Contactar
            </Button>
          )}
        </Space>
      ),
    },
    {
      title: 'Total',
      dataIndex: 'reg_total',
      key: 'reg_total',
      render: (total) => `$${parseFloat(total).toFixed(2)}`,
      align: 'right',
    },
    {
      title: 'Método de Pago',
      dataIndex: 'tipo_pago_nombre',
      key: 'tipo_pago_nombre',
      render: (tipo) => tipo || '-',
    },
    {
      title: 'Estado',
      dataIndex: 'reg_estado_pago',
      key: 'reg_estado_pago',
      render: (estado) => (
        <Tag color={getEstadoColor(estado)}>
          {estado}
        </Tag>
      ),
    },
    {
      title: 'Acciones',
      key: 'acciones',
      width: 200,
      render: (_, record) => (
        <Space>
          <Button
            type="link"
            icon={<EyeOutlined />}
            onClick={() => handleVerDetalle(record)}
          >
            Ver Detalle
          </Button>
          {record.reg_estado_pago === 'PENDIENTE' && (
            <>
              {/* Si el método de pago es EFECTIVO, mostrar botón de Aprobar */}
              {record.tipo_pago_nombre === 'EFECTIVO' ? (
                <Popconfirm
                  title="¿Aprobar este pago?"
                  description="Se marcará el pago como cobrado."
                  onConfirm={() => handleAprobarPago(record)}
                  okText="Sí, aprobar"
                  cancelText="Cancelar"
                >
                  <Button
                    type="link"
                    icon={<CheckCircleOutlined />}
                    style={{ color: '#52c41a' }}
                  >
                    Aprobar
                  </Button>
                </Popconfirm>
              ) : (
                <Button
                  type="link"
                  icon={<DollarOutlined />}
                  onClick={() => handleRegistrarPago(record)}
                  style={{ color: '#52c41a' }}
                >
                  Registrar Pago
                </Button>
              )}
            </>
          )}
        </Space>
      ),
    },
  ];

  return (
    <MainLayout>
      <div style={{ padding: '24px' }}>
        <div style={{ marginBottom: 16 }}>
          <h1 style={{ margin: 0, marginBottom: 16, color: '#ffffff', fontSize: isMobile ? '20px' : '24px' }}>
            Registro de Pagos
          </h1>
          <Space wrap style={{ marginBottom: 16 }}>
            <Button
              icon={<ReloadOutlined />}
              onClick={() => loadRegistros(filtroNombreAlumno || null, pagination.current, pagination.pageSize)}
              loading={loading}
            >
              {isMobile ? null : 'Actualizar'}
            </Button>
            <Button
              type="primary"
              icon={<PlusOutlined />}
              onClick={handleAbrirNuevoPago}
            >
              Nuevo pago
            </Button>
          </Space>
          
          {/* Filtro de búsqueda */}
          <div style={{ 
            marginBottom: 16, 
            padding: 16, 
            backgroundColor: '#2d2d2d', 
            borderRadius: 8,
            display: 'flex',
            flexDirection: isMobile ? 'column' : 'row',
            gap: 12,
            alignItems: isMobile ? 'stretch' : 'flex-end'
          }}>
            <div style={{ flex: 1, minWidth: isMobile ? '100%' : '300px' }}>
              <label style={{ display: 'block', marginBottom: 4, color: '#ffffff', fontSize: '14px' }}>
                Buscar por nombre de alumno
              </label>
              <Input
                placeholder="Ej: valen, longo, valentin..."
                value={filtroNombreAlumno}
                onChange={handleFiltroNombreAlumnoChange}
                allowClear
                style={{ width: '100%' }}
              />
            </div>
            {filtroNombreAlumno && (
              <Button
                onClick={handleLimpiarFiltro}
                style={{ minWidth: isMobile ? '100%' : 'auto' }}
              >
                Limpiar Filtro
              </Button>
            )}
          </div>
        </div>

        {isMobile ? (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
            {registros.length === 0 ? (
              <Empty description="No hay registros de pago" />
            ) : (
              registros.map(registro => (
                <Card
                  key={registro.reg_id}
                  hoverable
                  style={{
                    borderLeft: '4px solid #1890ff',
                    borderRadius: '8px',
                  }}
                >
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                      <div style={{ flex: 1 }}>
                        <div style={{ fontWeight: 'bold', fontSize: '16px', marginBottom: '8px', color: '#ffffff' }}>
                          {registro.alumno_nombre}
                        </div>
                        <div style={{ fontSize: '13px', color: '#b0b0b0', marginBottom: '4px' }}>
                          Mes/Año: {formatMesAnio(registro.reg_mes, registro.reg_anio)}
                        </div>
                        <div style={{ fontSize: '13px', color: '#b0b0b0', marginBottom: '4px' }}>
                          Plan: {registro.plan_nombre || '-'}
                        </div>
                        <div style={{ fontSize: '13px', color: '#b0b0b0', marginBottom: '4px', display: 'flex', alignItems: 'center', gap: '8px' }}>
                          <span>Teléfono: {registro.alumno_telefono || '-'}</span>
                          {registro.alumno_telefono && (
                            <Button
                              type="link"
                              icon={<MessageOutlined />}
                              onClick={() => handleContactarWhatsApp(registro.alumno_telefono)}
                              style={{ 
                                padding: 0, 
                                height: 'auto',
                                color: '#25D366'
                              }}
                            >
                              Contactar
                            </Button>
                          )}
                        </div>
                        <div style={{ fontSize: '13px', color: '#b0b0b0', marginBottom: '4px' }}>
                          Total: ${parseFloat(registro.reg_total).toFixed(2)}
                        </div>
                        <div style={{ fontSize: '13px', color: '#b0b0b0', marginBottom: '4px' }}>
                          Método: {registro.tipo_pago_nombre || '-'}
                        </div>
                        <div style={{ fontSize: '13px', color: '#b0b0b0', marginBottom: '4px' }}>
                          Fecha: {formatFecha(registro.reg_fecha_creacion)}
                        </div>
                      </div>
                      <Tag color={getEstadoColor(registro.reg_estado_pago)}>
                        {registro.reg_estado_pago}
                      </Tag>
                    </div>
                    <Space wrap style={{ width: '100%' }}>
                      <Button
                        type="link"
                        icon={<EyeOutlined />}
                        onClick={() => handleVerDetalle(registro)}
                      >
                        Ver Detalle
                      </Button>
                      {registro.reg_estado_pago === 'PENDIENTE' && (
                        <>
                          {/* Si el método de pago es EFECTIVO, mostrar botón de Aprobar */}
                          {registro.tipo_pago_nombre === 'EFECTIVO' ? (
                            <Popconfirm
                              title="¿Aprobar este pago?"
                              description="Se marcará el pago como cobrado."
                              onConfirm={() => handleAprobarPago(registro)}
                              okText="Sí, aprobar"
                              cancelText="Cancelar"
                            >
                              <Button
                                type="link"
                                icon={<CheckCircleOutlined />}
                                style={{ color: '#52c41a' }}
                              >
                                Aprobar
                              </Button>
                            </Popconfirm>
                          ) : (
                            <Button
                              type="link"
                              icon={<DollarOutlined />}
                              onClick={() => handleRegistrarPago(registro)}
                              style={{ color: '#52c41a' }}
                            >
                              Registrar Pago
                            </Button>
                          )}
                        </>
                      )}
                    </Space>
                  </div>
                </Card>
              ))
            )}
            {pagination.total > 0 && (
              <div style={{ marginTop: 16, display: 'flex', justifyContent: 'center' }}>
                <Pagination
                  current={pagination.current}
                  pageSize={pagination.pageSize}
                  total={pagination.total}
                  onChange={(page, pageSize) => {
                    setPagination(prev => ({ ...prev, current: page, pageSize }));
                    loadRegistros(filtroNombreAlumno || null, page, pageSize);
                  }}
                  onShowSizeChange={(current, size) => {
                    setPagination(prev => ({ ...prev, current: 1, pageSize: size }));
                    loadRegistros(filtroNombreAlumno || null, 1, size);
                  }}
                  showSizeChanger
                  pageSizeOptions={['10', '20', '50', '100']}
                  showTotal={(total) => `Total: ${total} registros`}
                />
              </div>
            )}
          </div>
        ) : (
          <Table
            columns={columns}
            dataSource={registros}
            loading={loading}
            rowKey="reg_id"
            pagination={{
              current: pagination.current,
              pageSize: pagination.pageSize,
              total: pagination.total,
              showSizeChanger: true,
              showTotal: (total) => `Total: ${total} registros`,
              pageSizeOptions: ['10', '20', '50', '100'],
            }}
            onChange={handleTableChange}
          />
        )}

        {/* Modal de detalle */}
        <Modal
          title="Detalle del Registro de Pago"
          open={modalDetalleVisible}
          onCancel={() => {
            setModalDetalleVisible(false);
            setRegistroSeleccionado(null);
          }}
          footer={[
            <Button key="close" onClick={() => {
              setModalDetalleVisible(false);
              setRegistroSeleccionado(null);
            }}>
              Cerrar
            </Button>
          ]}
          width={800}
        >
          {registroSeleccionado && (
            <div>
              <Descriptions
                title="Información del Pago"
                bordered
                column={1}
                style={{ marginBottom: 24 }}
              >
                <Descriptions.Item label="Alumno">
                  {registroSeleccionado.alumno_nombre}
                </Descriptions.Item>
                {(registroSeleccionado.reg_mes != null && registroSeleccionado.reg_anio != null) && (
                  <Descriptions.Item label="Mes/Año">
                    {formatMesAnio(registroSeleccionado.reg_mes, registroSeleccionado.reg_anio)}
                  </Descriptions.Item>
                )}
                <Descriptions.Item label="Total">
                  ${parseFloat(registroSeleccionado.reg_total).toFixed(2)}
                </Descriptions.Item>
                <Descriptions.Item label="Método de Pago">
                  {registroSeleccionado.tipo_pago_nombre || '-'}
                </Descriptions.Item>
                <Descriptions.Item label="Estado">
                  <Tag color={getEstadoColor(registroSeleccionado.reg_estado_pago)}>
                    {registroSeleccionado.reg_estado_pago}
                  </Tag>
                </Descriptions.Item>
                <Descriptions.Item label="Fecha de Creación">
                  {formatFecha(registroSeleccionado.reg_fecha_creacion)}
                </Descriptions.Item>
                {registroSeleccionado.reg_fecha_pago && (
                  <Descriptions.Item label="Fecha de Pago">
                    {formatFecha(registroSeleccionado.reg_fecha_pago)}
                  </Descriptions.Item>
                )}
                {registroSeleccionado.reg_comprobante && (
                  <Descriptions.Item label="Comprobante">
                    <div>
                      {registroSeleccionado.reg_comprobante.startsWith('data:image/') ? (
                        <Image
                          width={200}
                          src={registroSeleccionado.reg_comprobante}
                          alt="Comprobante"
                          style={{ maxWidth: '100%', marginBottom: 8 }}
                        />
                      ) : (
                        <div style={{ 
                          padding: 16, 
                          backgroundColor: '#2d2d2d', 
                          borderRadius: 4,
                          marginBottom: 8,
                          textAlign: 'center'
                        }}>
                          <div style={{ fontSize: 48, marginBottom: 8 }}>📄</div>
                          <div style={{ color: '#ffffff' }}>Archivo PDF</div>
                        </div>
                      )}
                      <Button
                        type="primary"
                        onClick={() => handleDownloadComprobante(
                          registroSeleccionado.reg_comprobante,
                          registroSeleccionado.tipo_pago_nombre
                        )}
                        style={{ marginTop: 8 }}
                      >
                        Descargar Comprobante
                      </Button>
                    </div>
                  </Descriptions.Item>
                )}
              </Descriptions>

            </div>
          )}
        </Modal>

        {/* Modal para registrar pago */}
        <Modal
          title={`Registrar Pago - ${registroParaPago ? registroParaPago.alumno_nombre : ''}`}
          open={modalRegistrarPagoVisible}
          onOk={handleSaveRegistrarPago}
          onCancel={() => {
            setModalRegistrarPagoVisible(false);
            formRegistrarPago.resetFields();
            setComprobanteFile(null);
            setRegistroParaPago(null);
          }}
          okText="Registrar Pago"
          cancelText="Cancelar"
          width={600}
        >
          {registroParaPago && (
            <div style={{ marginBottom: 16, padding: 12, backgroundColor: '#f0f2f5', borderRadius: 4, color: '#000000' }}>
              <div><strong>Plan:</strong> {registroParaPago.plan_nombre}</div>
              <div><strong>Total:</strong> ${parseFloat(registroParaPago.reg_total).toFixed(2)}</div>
            </div>
          )}

          <Form
            form={formRegistrarPago}
            layout="vertical"
          >
            <Form.Item
              name="metodoPago"
              label="Método de Pago"
              rules={[
                { required: true, message: 'Debe seleccionar un método de pago' },
              ]}
            >
              <Select
                placeholder="Seleccione el método de pago"
                onChange={(value) => {
                  if (value !== 'TRANSFERENCIA') {
                    formRegistrarPago.setFieldsValue({ comprobante: null });
                    setComprobanteFile(null);
                  }
                }}
              >
                {tiposPago.map(tipo => (
                  <Select.Option key={tipo.tip_id} value={tipo.tip_nombre}>
                    {tipo.tip_nombre}
                  </Select.Option>
                ))}
              </Select>
            </Form.Item>

            <Form.Item
              noStyle
              shouldUpdate={(prevValues, currentValues) => 
                prevValues.metodoPago !== currentValues.metodoPago
              }
            >
              {({ getFieldValue }) => {
                const metodoPago = getFieldValue('metodoPago');
                const mostrarComprobante = metodoPago === 'TRANSFERENCIA';
                
                return mostrarComprobante ? (
                  <Form.Item
                    name="comprobante"
                    label="Comprobante de Transferencia"
                    rules={[
                      { required: true, message: 'Debe cargar el comprobante de transferencia' },
                    ]}
                  >
                    <Upload
                      accept="image/*,application/pdf"
                      beforeUpload={async (file) => {
                        try {
                          // Comprimir el archivo
                          const compressedFile = await compressFile(file);
                          setComprobanteFile(compressedFile);
                          // Actualizar el campo del formulario para que la validación funcione
                          formRegistrarPago.setFieldsValue({ comprobante: compressedFile.name });
                          return false; // Prevent upload
                        } catch (error) {
                          message.error(error.message || 'Error al procesar el archivo');
                          return false;
                        }
                      }}
                      onRemove={() => {
                        setComprobanteFile(null);
                        formRegistrarPago.setFieldsValue({ comprobante: null });
                      }}
                      maxCount={1}
                    >
                      <Button icon={<UploadOutlined />}>Seleccionar Archivo (Imagen o PDF)</Button>
                    </Upload>
                    <div style={{ fontSize: '12px', color: '#b0b0b0', marginTop: 4 }}>
                      Formatos aceptados: JPG, PNG, PDF (máx. 5MB)
                    </div>
                  </Form.Item>
                ) : null;
              }}
            </Form.Item>
          </Form>
        </Modal>

        {/* Modal Nuevo pago mensual */}
        <Modal
          title="Nuevo pago mensual"
          open={modalNuevoPagoVisible}
          onOk={handleSaveNuevoPago}
          onCancel={() => {
            setModalNuevoPagoVisible(false);
            formNuevoPago.resetFields();
            setComprobanteFileNuevo(null);
          }}
          okText="Registrar pago"
          cancelText="Cancelar"
          width={520}
        >
          <div style={{ 
            marginBottom: 16, 
            padding: 12, 
            background: '#162312', 
            border: '1px solid #274916', 
            borderRadius: 8,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between'
          }}>
            <span style={{ color: '#8c8c8c', fontSize: 14 }}>Valor del plan:</span>
            <span style={{ color: '#52c41a', fontWeight: 700, fontSize: 18 }}>
              {planNuevoPago
                ? new Intl.NumberFormat('es-AR', { style: 'currency', currency: 'ARS' }).format(Number(planNuevoPago.pla_valor || 0))
                : 'Seleccione un plan'}
            </span>
          </div>
          <Form form={formNuevoPago} layout="vertical">
            <Form.Item
              name="alumnoId"
              label="Alumno"
              rules={[{ required: true, message: 'Seleccione un alumno' }]}
            >
              <Select
                placeholder="Seleccione alumno"
                showSearch
                optionFilterProp="children"
                filterOption={(input, option) =>
                  (option?.label ?? '').toLowerCase().includes(input.toLowerCase())
                }
                options={alumnos.map(a => ({
                  value: a.alu_id,
                  label: `${a.alu_nombre} ${a.alu_apellido}`,
                }))}
              />
            </Form.Item>
            <Form.Item
              name="planId"
              label="Mensualidad / Plan"
              rules={[{ required: true, message: 'Seleccione la mensualidad' }]}
            >
              <Select
                placeholder="Seleccione mensualidad"
                showSearch
                optionFilterProp="label"
                options={planes.map(plan => ({
                  value: plan.pla_id,
                  label: `${plan.pla_nombre} - ${new Intl.NumberFormat('es-AR', { style: 'currency', currency: 'ARS' }).format(Number(plan.pla_valor || 0))}`,
                }))}
              />
            </Form.Item>
            <Form.Item
              name="mes"
              label="Mes"
              rules={[{ required: true, message: 'Seleccione el mes' }]}
            >
              <Select
                placeholder="Mes"
                options={MESES.map(m => ({ value: m.value, label: m.label }))}
              />
            </Form.Item>
            <Form.Item
              name="anio"
              label="Año"
              rules={[{ required: true, message: 'Ingrese el año' }]}
            >
              <Select
                placeholder="Año"
                options={Array.from({ length: 5 }, (_, i) => new Date().getFullYear() - i).map(y => ({
                  value: y,
                  label: String(y),
                }))}
              />
            </Form.Item>
            <Form.Item
              name="metodoPago"
              label="Forma de pago"
              rules={[{ required: true, message: 'Seleccione la forma de pago' }]}
            >
              <Select
                placeholder="Forma de pago"
                onChange={(value) => {
                  if (value !== 'TRANSFERENCIA') {
                    formNuevoPago.setFieldsValue({ comprobante: null });
                    setComprobanteFileNuevo(null);
                  }
                }}
              >
                {tiposPago.map(tipo => (
                  <Select.Option key={tipo.tip_id} value={tipo.tip_nombre}>
                    {tipo.tip_nombre}
                  </Select.Option>
                ))}
              </Select>
            </Form.Item>
            <Form.Item
              noStyle
              shouldUpdate={(prev, curr) => prev.metodoPago !== curr.metodoPago}
            >
              {({ getFieldValue }) =>
                getFieldValue('metodoPago') === 'TRANSFERENCIA' ? (
                  <Form.Item
                    name="comprobante"
                    label="Comprobante de transferencia"
                    rules={[{ required: true, message: 'Cargue el comprobante' }]}
                  >
                    <Upload
                      accept="image/*,application/pdf"
                      beforeUpload={async (file) => {
                        try {
                          const compressedFile = await compressFile(file);
                          setComprobanteFileNuevo(compressedFile);
                          formNuevoPago.setFieldsValue({ comprobante: compressedFile.name });
                          return false;
                        } catch (err) {
                          message.error(err.message || 'Error al procesar el archivo');
                          return false;
                        }
                      }}
                      onRemove={() => {
                        setComprobanteFileNuevo(null);
                        formNuevoPago.setFieldsValue({ comprobante: null });
                      }}
                      maxCount={1}
                    >
                      <Button icon={<UploadOutlined />}>Seleccionar archivo (imagen o PDF)</Button>
                    </Upload>
                  </Form.Item>
                ) : null
              }
            </Form.Item>
          </Form>
        </Modal>
      </div>
    </MainLayout>
  );
};

export default RegistroPagos;

